import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export interface AuthSelectOption {
  value: string;
  label: string;
}

interface AuthFloatingSelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'children' | 'multiple'
> {
  /** Visible, floating label. Also the accessible name (via htmlFor). */
  label: string;
  /** Required: drives htmlFor and the error element's id. */
  id: string;
  /** Controlled value. Empty string means "nothing chosen yet". */
  value: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  options: ReadonlyArray<AuthSelectOption>;
  /** Text for the disabled empty option. When set, the label stays floated. */
  placeholder?: string;
  error?: string;
}

/**
 * The floating-label sibling of `AuthFloatingInput`, wrapping a NATIVE
 * `<select>`.
 *
 * Native on purpose: it gives the platform picker on mobile, is keyboard and
 * screen-reader solid for free, and sidesteps the portal/clipping problems the
 * custom dropdowns elsewhere in the app run into.
 *
 * A `<select>` is never "placeholder shown" in CSS terms, so the label's
 * floated state is computed from the value/focus instead of `peer-*`.
 * Everything else — sizes, border colours, and the `bg-white dark:bg-grey-950`
 * label chip that punches a hole through the field border on the auth
 * surface — is copied from `AuthFloatingInput` so the two sit flush together.
 */
const AuthFloatingSelect = React.forwardRef<HTMLSelectElement, AuthFloatingSelectProps>(
  ({ label, error, options, placeholder, className = '', onFocus, onBlur, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);

    const errorId = `${props.id}-error`;
    const describedBy =
      [props['aria-describedby'], error ? errorId : undefined].filter(Boolean).join(' ') ||
      undefined;

    const hasValue = props.value !== '';
    // With a placeholder there is already text in the field, so the label must
    // stay up or the two would overlap.
    const floated = hasValue || isFocused || Boolean(placeholder);

    const handleFocus = (event: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(true);
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    };

    return (
      <div className={className}>
        <div className="relative">
          <select
            {...props}
            ref={ref}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={`peer w-full h-14 pl-3.5 pr-10 pt-4 pb-2 text-[15px] leading-6 bg-white dark:bg-grey-950 rounded-lg border outline-none appearance-none transition-colors disabled:opacity-50 ${
              hasValue ? 'text-text-primary' : 'text-text-tertiary'
            } ${
              error
                ? 'border-error-500 focus:border-error-500'
                : 'border-border-primary/20 focus:border-primary-500'
            }`}
          >
            {/* Selectable only when the field is optional: otherwise a value once
                chosen for 'Company size (optional)' could never be unset again. */}
            <option value="" disabled={Boolean(props.required)}>
              {placeholder ?? ''}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {/* Floating label — htmlFor keeps it announced by screen readers */}
          <label
            htmlFor={props.id}
            className={`pointer-events-none absolute left-3 px-0.5 transition-[top,font-size,color] duration-150 bg-white dark:bg-grey-950 ${
              floated ? '-top-2 text-xs font-semibold' : 'top-4 text-[15px] font-normal'
            } ${
              error
                ? 'text-error-500'
                : isFocused
                  ? 'text-primary-500'
                  : floated
                    ? 'text-text-secondary'
                    : 'text-text-tertiary'
            }`}
          >
            {label}
          </label>
          <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
        </div>
        {/* role="alert" so a validation failure is announced, not just shown. */}
        {error && (
          <p id={errorId} role="alert" className="mt-1.5 text-xs text-error-500">
            {error}
          </p>
        )}
      </div>
    );
  }
);

AuthFloatingSelect.displayName = 'AuthFloatingSelect';

export default AuthFloatingSelect;
